import type { AvailableIconName } from '@/assets/icons';
import { DefaultScreen } from '@/components/template/default-screen';
import { SvgIcon, Text } from '@/components/ui';
import { Tabbar } from '@/components/ui/tabbar';
import { STRINGS } from '@/constants/strings';
import { GuardianProfileScreen } from '@/features/guardian-profile/screens';
import { GuardianHomeScreen } from '@/features/guardian/screens/guardian-home-screen';
import GuardianKidsScreen from '@/features/guardian/screens/guardian-kids-screen';
// Re-enable when the Kids booking experience is ready.
// import { GuardianKidsScreen } from '@/features/guardian/screens/guardian-kids-screen';
import { useOpenShop } from '@/features/shop/hooks/use-open-shop';
import { createStyles, useScaling } from '@/hooks';
import type { GuardianTabNavigatorParamList } from '@/navigations/types';
import { colors, guardianColors } from '@/theme/colors';
import { BottomTabBarProps, createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { memo, useCallback } from 'react';
import { Platform, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const Tab = createBottomTabNavigator<GuardianTabNavigatorParamList>();

const ACTIVE_COLOR = guardianColors['Main/Main'];
const INACTIVE_COLOR = guardianColors['6'];
const TAB_ICON_SIZE = 24;

type GuardianTabIconProps = Parameters<
  NonNullable<BottomTabBarProps['descriptors'][string]['options']['tabBarIcon']>
>[0];

const createTabIcon =
  (name: AvailableIconName, activeName: AvailableIconName = name, allowActiveIcon = false) =>
  ({ focused, color, size }: GuardianTabIconProps) => (
    <SvgIcon name={allowActiveIcon && focused ? activeName : name} size={size} color={color} />
  );

const homeOptions = { tabBarIcon: createTabIcon('IcHome1') };
const mobileHomeOptions = { tabBarIcon: createTabIcon('IcHome1', 'IcHome1Active', true) };
const bookOptions = { tabBarIcon: createTabIcon('IcBook') };
// The tab is still routed as `Kids`; only the label the guardian reads says "Players".
const kidsOptions = {
  tabBarLabel: STRINGS.common.navigation.tabPlayers,
  tabBarIcon: createTabIcon('IcKids'),
};
const mobileKidsOptions = { tabBarIcon: createTabIcon('IcKids', 'IcKidsActive', true) };
const shopOptions = { tabBarIcon: createTabIcon('IcShopping') };
const mobileShopOptions = {
  tabBarIcon: createTabIcon('IcShopping', 'IcShoppingActive', true),
};
const profileOptions = { tabBarIcon: createTabIcon('IcUserSettings') };
const mobileProfileOptions = {
  tabBarIcon: createTabIcon('IcUserSettings', 'IcUserSettingsActive', true),
};

const mobileOptionsByRouteName: Partial<
  Record<keyof GuardianTabNavigatorParamList, typeof mobileHomeOptions>
> = {
  Home: mobileHomeOptions,
  Kids: mobileKidsOptions,
  Shop: mobileShopOptions,
  Profile: mobileProfileOptions,
};

const GuardianMobileTabBar = memo(({ state, descriptors, navigation }: BottomTabBarProps) => {
  const { bottom } = useSafeAreaInsets();
  const { scale } = useScaling();
  const styles = useStyles();
  const tabBarBottomInset = Platform.OS === 'web' ? bottom + scale(26) : bottom;

  const handlePress = useCallback(
    (index: number, routeKey: string) => {
      const event = navigation.emit({
        type: 'tabPress',
        target: routeKey,
        canPreventDefault: true,
      });
      const isFocused = state.index === index;
      const route = state.routes[index];

      if (!isFocused && !event.defaultPrevented && route) {
        navigation.navigate(route.name, route.params);
      }
    },
    [navigation, state.index, state.routes],
  );

  return (
    <View style={[styles.tabBarContainer, { paddingBottom: tabBarBottomInset }]}>
      <View style={styles.tabBarContent}>
        {state.routes.map((route, index) => {
          const isFocused = state.index === index;
          const descriptorOptions = descriptors[route.key].options;
          const mobileOptions =
            mobileOptionsByRouteName[route.name as keyof GuardianTabNavigatorParamList];
          const options = mobileOptions
            ? { ...descriptorOptions, ...mobileOptions }
            : descriptorOptions;
          const color = isFocused ? ACTIVE_COLOR : INACTIVE_COLOR;
          const label =
            typeof options.tabBarLabel === 'string'
              ? options.tabBarLabel
              : options.title !== undefined
                ? options.title
                : route.name;
          const icon = options.tabBarIcon?.({
            focused: isFocused,
            color,
            size: scale(TAB_ICON_SIZE),
          }) ?? <View style={styles.iconPlaceholder} />;

          return (
            <Pressable
              key={route.key}
              accessibilityRole="button"
              accessibilityState={isFocused ? { selected: true } : {}}
              accessibilityLabel={options.tabBarAccessibilityLabel}
              testID={options.tabBarButtonTestID}
              onPress={() => handlePress(index, route.key)}
              style={({ pressed }) => [styles.tabButton, pressed ? styles.tabButtonPressed : null]}>
              <View style={styles.iconSlot}>{icon}</View>
              <Text
                numberOfLines={1}
                variant="Font Guardian App/Subtitle/Subtitle 10 Semibold"
                style={[styles.tabLabel, { color }]}>
                {label}
              </Text>
              <View style={[styles.activeIndicator, isFocused ? null : styles.inactiveIndicator]} />
            </Pressable>
          );
        })}
      </View>
    </View>
  );
});

const GuardianTabBar = memo((props: BottomTabBarProps) => {
  const { isTablet } = useScaling();

  if (isTablet) {
    return <Tabbar {...props} variant="guardian" />;
  }

  return <GuardianMobileTabBar {...props} />;
});

export default function GuardianTabNavigator() {
  const renderTabBar = useCallback((props: BottomTabBarProps) => <GuardianTabBar {...props} />, []);
  const openShop = useOpenShop();

  return (
    <Tab.Navigator
      tabBar={renderTabBar}
      screenOptions={{ headerShown: false, lazy: true, freezeOnBlur: true }}>
      <Tab.Screen name="Home" component={GuardianHomeScreen} options={homeOptions} />
      <Tab.Screen name="Book" component={DefaultScreen} options={bookOptions} />
      <Tab.Screen name="Kids" component={GuardianKidsScreen} options={kidsOptions} />
      <Tab.Screen
        name="Shop"
        component={DefaultScreen}
        options={shopOptions}
        listeners={{
          // Don't navigate to a screen — open the Shopify store in the system browser instead.
          tabPress: e => {
            e.preventDefault();
            // openShop() handles its own errors internally; the catch only placates the linter.
            openShop(
              'https://shopify.com/99670950163/account/logout?return_url=https%3A%2F%2Fuskg-sandbox.myshopify.com%2Fcustomer_authentication%2Flogin%3Freturn_to%3D%2F',
            ).catch(() => undefined);
          },
        }}
      />
      <Tab.Screen name="Profile" component={GuardianProfileScreen} options={profileOptions} />
    </Tab.Navigator>
  );
}

const useStyles = createStyles(({ scale }) => ({
  tabBarContainer: {
    backgroundColor: colors['0'],
    borderTopLeftRadius: scale(12),
    borderTopRightRadius: scale(12),
    borderCurve: 'continuous',
    paddingTop: scale(12),
    paddingHorizontal: scale(8),
    shadowColor: colors['10'],
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
    elevation: 16,
  },
  tabBarContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  tabButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
  tabButtonPressed: {
    opacity: 1,
  },
  iconSlot: {
    alignItems: 'center',
    justifyContent: 'center',
    width: scale(24),
    height: scale(24),
  },
  iconPlaceholder: {
    width: scale(TAB_ICON_SIZE),
    height: scale(TAB_ICON_SIZE),
  },
  tabLabel: { textAlign: 'center' },
  activeIndicator: {
    width: scale(12),
    height: scale(1),
    borderRadius: scale(2),
    borderCurve: 'continuous',
    backgroundColor: ACTIVE_COLOR,
  },
  inactiveIndicator: {
    opacity: 0,
  },
}));
